import "../styleSheets/attachmentViewEdit.css";
import "../styleSheets/commonElements.css";
import "../styleSheets/buttons.css";
import {useTranslation} from "react-i18next";
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import React, {useMemo} from "react";
import {capitalizeFirst, convertUtcToLocal, getFileTypeIcon, getSortedAssociatedMessages} from "../utilities/utilities";
import type {Attachment} from "../API";

type AttachmentRow = Attachment;

function AttachmentView() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation("common");
    const attachment = (location.state as AttachmentRow) || null;
    const Icon = getFileTypeIcon(attachment?.type ?? "");
    const displayFileName = attachment?.s3Key?.split('/').pop() ?? "";
    const onBack = () => navigate(-1);
    const onEdit = () => navigate(`/AttachmentEdit/${attachment.id}`, { state: attachment });
    const associatedMessages = useMemo(
        () => getSortedAssociatedMessages(attachment),
        [attachment]
    );
    if (!attachment) {
        return <Navigate to="/Panel?selectedTab=files" replace />;
    }

    return (
        <div className="attachment-view-page">
            {/* Top nav */}
            <div className="attachment-topbar">
                <div className="attachment-topbar__nav">
                    <div className="attachment-topbar__container">
                        <div className="attachment-topbar__row">
                            <button
                                type="button"
                                className="btn btn--gray btn--size-md btn--icon-left"
                                onClick={onBack}
                            >
                                <div className="btn__icon icon-chevron-left" aria-hidden="true" />
                                {t("common.back")}
                            </button>

                            <div className="attachment-topbar__title">
                                {capitalizeFirst(t("file.file"))}
                            </div>

                            <div className="attachment-topbar__actions">
                                <button
                                    type="button"
                                    className="btn btn--white btn--size-md"
                                    onClick={onEdit}
                                >
                                    {t("file.editFile")}
                                </button>
                            </div>
                        </div>

                        {/* Small-screen title (same pattern as Recipient/Message pages) */}
                        <div className="attachment-topbar__titleBelow">
                            {capitalizeFirst(t("file.file"))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="section-attachment-details">
                <div className="attachment-details">
                    <div className="attachment-details-title">{t("file.fileDetails")}</div>

                    <div className="attachment-info-container">
                        <Icon className="attachment-icon" aria-hidden="true" />
                        <div className="attachment-metadata-container">
                            <div className="attachment-name">{displayFileName}</div>
                            <div className="attachment-metadata-element-container">
                                <div className="attachment-metadata-element">Status: {attachment?.status}</div>
                                <div className="attachment-metadata-element">| Type: {attachment?.type}</div>
                                <div className="attachment-metadata-element"> | Size : {attachment?.size} Mb</div>
                                <div className="attachment-metadata-element">
                                    {" "}
                                    | Uploaded on : {convertUtcToLocal(attachment?.uploadDate, "NoDay")}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="attachment-details-subtitle-container">
                        <div className="attachment-details-subtitle">{capitalizeFirst(t("file.alias"))}</div>
                        <div className="attachment-data">{attachment?.name}</div>
                    </div>

                    <div className="attachment-details-subtitle-container">
                        <div className="attachment-details-subtitle">{t("file.tableColumn.description")}</div>
                        <div className="attachment-data">{attachment?.description}</div>
                    </div>
                </div>

                <div className="associated-messages-container">
                    <div className="attachment-details-title">{t("common.associatedMessages")}</div>

                    <div className="associated-messages">
                        {associatedMessages.length === 0 ? (
                            <div className="associated-message associated-message--empty">
                                {t("common.noAssociatedMessages")}
                            </div>
                        ) : (
                            associatedMessages.map((m: any) => (
                                <div key={m.message.id} className="associated-message">
                                    {m.message.title ?? t("message.untitledMessage")}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
export default AttachmentView;
